import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, Bed, Bathtub, Ruler, MapPin, CheckCircle, Check,
  Drop, PersonSimpleWalk, GraduationCap, ShieldWarning, Info,
  CalendarPlus, ShareNetwork,
} from "@phosphor-icons/react";
import api, { formatApiErrorDetail, FALLBACK_LISTING_IMG, API } from "@/lib/apiClient";
import { toast } from "sonner";
import SiteNav from "@/components/SiteNav";
import MortgageCalculator from "@/components/MortgageCalculator";
import ListingImage from "@/components/ListingImage";
import PropertyContextBadges from "@/components/PropertyContextBadges";
import { formatListingPrice } from "@/lib/constants";
import Seo, { listingJsonLd } from "@/lib/seo";
import { track } from "@/lib/tracking";

function tourEmbedSrc(url) {
  if (!url) return null;
  try {
    const u = new URL(url);
    if (u.hostname.includes("youtube.com") && u.searchParams.get("v")) return `https://www.youtube.com/embed/${u.searchParams.get("v")}`;
    if (u.hostname.includes("youtu.be")) return `https://www.youtube.com/embed${u.pathname}`;
    return url;
  } catch {
    return url;
  }
}

function InquiryForm({ property }) {
  const isRent = property.listing_type === "rent";
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    message: isRent
      ? `I'm interested in renting ${property.title}. Please send me more details.`
      : `I'm interested in ${property.title}. Please send me more details.`,
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    // Phone optional — requiring it was killing conversions on hot listings.
    if (!form.name || !form.email) {
      toast.error("Please fill in your name and email");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/leads", {
        lead_type: isRent ? "renter" : "buyer",
        ...form,
        property_id: property.id,
        property_title: property.title,
        areas: [property.region],
        property_type: [property.property_type],
        budget: isRent ? `${property.price}/mo` : undefined,
      });
      setDone(true);
      track("inquiry", { listing_type: property.listing_type });
      toast.success("Inquiry sent! Lamar will reach out shortly.");
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="text-center py-8" data-testid="inquiry-success">
        <CheckCircle size={48} weight="duotone" className="mx-auto text-brand mb-4" />
        <h3 className="text-xl text-white mb-2">Inquiry sent</h3>
        <p className="text-white/60 text-sm">Lamar received your interest in this home and will contact you shortly.</p>
        <Link
          to={`/book?property_id=${property.id}&topic=${encodeURIComponent(`Tour: ${property.title || ""}`)}`}
          data-testid="inquiry-book-showing"
          className="mt-5 inline-flex items-center gap-2 bg-brand text-black px-6 py-3 rounded-sm text-xs uppercase tracking-wider font-medium hover:bg-brand-hover"
        >
          <CalendarPlus size={15} weight="bold" /> Pick a showing time now
        </Link>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="inquiry-form">
      <h3 className="text-xl text-white">{isRent ? "Inquire about this rental" : "Inquire about this home"}</h3>
      <div>
        <label className="lux-label">Full name</label>
        <input data-testid="inquiry-name" className="lux-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Jane Doe" />
      </div>
      <div>
        <label className="lux-label">Email</label>
        <input data-testid="inquiry-email" type="email" className="lux-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@email.com" />
      </div>
      <div>
        <label className="lux-label">Phone</label>
        <input data-testid="inquiry-phone" type="tel" className="lux-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+1 (555) 123-4567" />
      </div>
      <div>
        <label className="lux-label">Message</label>
        <textarea data-testid="inquiry-message" rows={3} className="lux-input resize-none" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
      </div>
      <button
        type="submit"
        disabled={submitting}
        data-testid="inquiry-submit"
        className="w-full bg-brand text-black py-4 rounded-sm text-sm uppercase tracking-[0.1em] font-medium hover:bg-brand-hover transition-colors disabled:opacity-60"
      >
        {submitting ? "Sending…" : "Request details"}
      </button>
      <Link
        to={`/book?property_id=${property.id}&topic=${encodeURIComponent(`Tour: ${property.title || ""}`)}`}
        data-testid="inquiry-book-link"
        className="block text-center w-full border border-brand/40 text-brand py-3.5 rounded-sm text-sm uppercase tracking-[0.1em] hover:bg-brand/10 transition-colors"
      >
        <CalendarPlus size={15} weight="bold" className="inline mr-2 -mt-0.5" /> Book a showing
      </Link>
    </form>
  );
}

const FLOOD_LABEL_MAP = { low: "Low", medium: "Medium", high: "High", unknown: "N/A" };
const WALK_LABEL_MAP = { low: "Car-friendly", medium: "Walkable", high: "Very walkable" };
const NYC_REGIONS = new Set(["The Bronx", "Manhattan", "Brooklyn", "Queens", "Staten Island"]);

export default function PropertyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [property, setProperty] = useState(null);
  const [context, setContext] = useState(null);
  const [activeImg, setActiveImg] = useState(0);

  useEffect(() => {
    api.get(`/properties/${id}`).then(({ data }) => setProperty(data)).catch(() => {
      toast.error("Property not found");
      navigate("/listings");
    });
    // Full open-data context loads separately so slower FEMA/NYC lookups never
    // block the hero. Errors here just leave the section in its N/A state.
    api.get(`/properties/${id}/neighborhood`)
      .then(({ data }) => setContext(data))
      .catch(() => {});
  }, [id, navigate]);

  if (!property) {
    return (
      <div className="bg-background min-h-screen">
        <SiteNav />
        <div className="pt-28 flex justify-center"><div className="h-8 w-8 rounded-full border-2 border-brand border-t-transparent animate-spin" /></div>
      </div>
    );
  }

  const images = property.images?.length ? property.images : [FALLBACK_LISTING_IMG];
  const isNycContextArea = NYC_REGIONS.has(property.region);

  const flood = context?.flood || {};
  const walk = context?.walk || {};
  const schoolsPayload = context?.schools || {};
  const crime = context?.crime || {};
  const nearestSchools = Array.isArray(schoolsPayload.schools) ? schoolsPayload.schools : [];
  const floodBand = String(property.flood_risk_band || flood.risk || "unknown").toLowerCase();
  const walkBand = property.walkability_band || walk.band || null;
  const walkabilityLabel = walkBand ? WALK_LABEL_MAP[walkBand] : "N/A";
  const floodLabel = property.flood_risk_label || flood.label || FLOOD_LABEL_MAP[floodBand] || "N/A";
  const districtValue = property.school_district ?? schoolsPayload.district ?? null;
  const schoolDistrictLabel = districtValue || "N/A";
  const floodZone = property.flood_zone || flood.zone || null;

  const shareListing = async () => {
    // OG-tagged share URL renders a rich preview in iMessage/FB, then redirects here
    const shareUrl = `${API}/share/listing/${property.id}`;
    const title = `${property.title || property.address} — The Whyte Glove`;
    try {
      if (navigator.share) {
        await navigator.share({ title, url: shareUrl });
      } else {
        await navigator.clipboard.writeText(shareUrl);
        toast.success("Share link copied");
      }
      track("share", { listing: property.id });
    } catch {
      // user dismissed share sheet
    }
  };

  return (
    <div className="bg-background min-h-screen">
      <Seo
        title={`${property.title || property.address}`}
        description={`${formatListingPrice(property.price, property.listing_type)} · ${property.bedrooms || "?"} bd / ${property.bathrooms || "?"} ba in ${property.region}. Presented by Lamar A. Whyte, The Whyte Glove.`}
        path={`/listings/${property.id}`}
        jsonLd={listingJsonLd(property)}
        jsonLdId="listing"
      />
      <SiteNav />
      <div className="max-w-7xl mx-auto px-6 pt-28 pb-16" data-testid="property-detail">
        <div className="flex items-center justify-between mb-6">
          <Link to="/listings" className="inline-flex items-center gap-2 text-sm text-white/50 hover:text-white transition-colors">
            <ArrowLeft size={16} /> Back to listings
          </Link>
          <button
            type="button"
            onClick={shareListing}
            data-testid="share-listing-btn"
            className="inline-flex items-center gap-2 text-sm text-white/50 hover:text-brand transition-colors"
          >
            <ShareNetwork size={16} /> Share
          </button>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left: gallery + details */}
          <div className="lg:col-span-2 space-y-6">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="rounded-sm overflow-hidden aspect-[16/10] bg-[#101116]">
              <ListingImage src={images[activeImg]} alt={property.title} className="w-full h-full object-cover" data-testid="detail-hero-image" />
            </motion.div>
            {images.length > 1 && (
              <div className="flex gap-3">
                {images.map((img, i) => (
                  <button key={i} onClick={() => setActiveImg(i)} className={`h-20 w-28 rounded-sm overflow-hidden border-2 transition-colors ${activeImg === i ? "border-brand" : "border-transparent opacity-60 hover:opacity-100"}`}>
                    <ListingImage src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}

            <div>
              <div className="flex flex-wrap items-center gap-3 mb-2">
                <span className="text-3xl font-serif text-brand">{formatListingPrice(property.price, property.listing_type)}</span>
                <span className="px-2.5 py-1 text-[11px] uppercase tracking-wider rounded-sm border border-white/15 text-white/60">{property.property_type}</span>
                {property.listing_type === "rent" && (
                  <span className="px-2.5 py-1 text-[11px] uppercase tracking-wider rounded-sm border border-brand/30 text-brand bg-brand/10" data-testid="detail-rent-badge">
                    For Rent
                  </span>
                )}
                {property.no_fee && (
                  <span className="px-2.5 py-1 text-[11px] uppercase tracking-wider rounded-sm border border-brand/40 text-brand bg-brand/15" data-testid="detail-no-fee-badge">
                    No fee
                  </span>
                )}
                {property.months_free ? (
                  <span className="px-2.5 py-1 text-[11px] uppercase tracking-wider rounded-sm border border-white/15 text-white/70" data-testid="detail-months-free-badge">
                    {Number(property.months_free)} month{Number(property.months_free) === 1 ? "" : "s"} free
                  </span>
                ) : null}
                {property.pets_allowed && (
                  <span className="px-2.5 py-1 text-[11px] uppercase tracking-wider rounded-sm border border-white/15 text-white/70" data-testid="detail-pets-badge">
                    Pets allowed
                  </span>
                )}
                {property.source === "borough" && (
                  <span className="px-2.5 py-1 text-[11px] uppercase tracking-wider rounded-sm border border-brand/30 text-brand bg-brand/10" data-testid="detail-live-badge">
                    Live NYC market
                  </span>
                )}
              </div>
              <h1 className="text-3xl text-white mb-2">{property.title}</h1>
              <p className="flex items-center gap-1.5 text-white/50">
                <MapPin size={15} weight="duotone" className="text-brand shrink-0" />
                {property.address}
              </p>
              {property.neighborhood && property.region && (
                <p className="mt-1 text-xs uppercase tracking-wider text-white/35" data-testid="detail-location-meta">
                  {property.neighborhood} · {property.region}
                </p>
              )}
              <PropertyContextBadges property={property} showUnavailable className="mt-4" />
              {(property.available_at || property.lease_term_months || property.net_effective) && (
                <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-sm text-white/45" data-testid="detail-rental-terms">
                  {property.available_at && <span>Available: {property.available_at}</span>}
                  {property.lease_term_months ? <span>Lease: {property.lease_term_months} months</span> : null}
                  {property.net_effective ? (
                    <span>Net effective: {formatListingPrice(property.net_effective, "rent")}</span>
                  ) : null}
                </div>
              )}
              {property.source_label && (
                <p className="mt-2 text-xs text-white/30">Listing source: {property.source_label}</p>
              )}
            </div>

            <div className="flex flex-wrap gap-8 py-5 border-y border-white/5">
              <span className="flex items-center gap-2 text-white/80"><Bed size={20} weight="duotone" className="text-brand" /> {property.bedrooms === 0 ? "Studio" : `${property.bedrooms} Beds`}</span>
              <span className="flex items-center gap-2 text-white/80"><Bathtub size={20} weight="duotone" className="text-brand" /> {property.bathrooms} Baths</span>
              {property.sqft ? <span className="flex items-center gap-2 text-white/80"><Ruler size={20} weight="duotone" className="text-brand" /> {property.sqft.toLocaleString()} sqft</span> : null}
              <span className="flex items-center gap-2 text-white/80"><MapPin size={20} weight="duotone" className="text-brand" /> {property.region}</span>
            </div>

            {property.description && (
              <div>
                <h2 className="text-sm uppercase tracking-[0.18em] text-white/50 mb-3">
                  {property.listing_type === "rent" ? "About this rental" : "About this home"}
                </h2>
                <p className="text-white/70 leading-relaxed font-light">{property.description}</p>
              </div>
            )}

            <section className="space-y-4" data-testid="detail-context-section">
              <div>
                <h2 className="text-sm uppercase tracking-[0.18em] text-white/50 mb-3">Neighborhood context</h2>
                <p className="text-sm text-white/45 max-w-3xl">
                  Public open-data context for the immediate area. NYC-specific layers degrade gracefully outside the five boroughs.
                </p>
              </div>
              <div className="grid gap-4 md:grid-cols-3">
                <div className="rounded-sm border border-white/5 bg-[#101116] p-4" data-testid="detail-flood-card">
                  <p className="text-[11px] uppercase tracking-[0.18em] text-white/35 mb-2 flex items-center gap-1.5">
                    <Drop size={12} weight="duotone" className="text-brand" /> Flood &amp; storm
                  </p>
                  <p className="text-white text-lg">{floodLabel}</p>
                  <p className="text-sm text-white/45 mt-2">
                    {floodZone
                      ? `${floodZone}${flood.source ? ` · ${flood.source}` : ""}.`
                      : isNycContextArea
                        ? "Outside mapped NYC evacuation zones or not currently matched."
                        : "N/A outside NYC-specific flood layers."}
                  </p>
                </div>
                <div className="rounded-sm border border-white/5 bg-[#101116] p-4" data-testid="detail-walk-card">
                  <p className="text-[11px] uppercase tracking-[0.18em] text-white/35 mb-2 flex items-center gap-1.5">
                    <PersonSimpleWalk size={12} weight="duotone" className="text-brand" /> Walkability
                  </p>
                  <p className="text-white text-lg">{walkabilityLabel}</p>
                  <p className="text-sm text-white/45 mt-2">
                    Free proxy: transit distance + amenity density. Not Walk Score®.
                    {property.nearest_transit_name
                      ? ` Nearest transit: ${property.nearest_transit_name} · ~${property.nearest_transit_miles} mi.`
                      : ""}
                  </p>
                </div>
                <div className="rounded-sm border border-white/5 bg-[#101116] p-4" data-testid="detail-school-district">
                  <p className="text-[11px] uppercase tracking-[0.18em] text-white/35 mb-2 flex items-center gap-1.5">
                    <GraduationCap size={12} weight="duotone" className="text-brand" /> School district
                  </p>
                  <p className="text-white text-lg">{schoolDistrictLabel}</p>
                  <p className="text-sm text-white/45 mt-2">
                    {districtValue
                      ? "NYC Community School District. Nearest schools listed below."
                      : isNycContextArea
                        ? "District match not available for this point right now."
                        : "N/A outside NYC DOE district data."}
                  </p>
                </div>
              </div>

              <div className="rounded-sm border border-white/5 bg-[#101116] p-5" data-testid="detail-schools-list">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between mb-4">
                  <div>
                    <p className="text-[11px] uppercase tracking-[0.18em] text-white/35">Public school context</p>
                    <h3 className="text-white text-xl">Nearby NYC DOE schools</h3>
                  </div>
                  {districtValue && (
                    <p className="text-sm text-brand">{districtValue}</p>
                  )}
                </div>
                {nearestSchools.length > 0 ? (
                  <div className="grid gap-3 md:grid-cols-3">
                    {nearestSchools.slice(0, 5).map((school, i) => (
                      <div key={`${school.name}-${i}`} className="rounded-sm border border-white/5 bg-black/20 p-4">
                        <p className="text-white text-sm">{school.name || "—"}</p>
                        <p className="text-white/40 text-xs mt-1">{school.address || ""}</p>
                        <p className="text-white/55 text-xs mt-3">
                          {school.distance_miles != null ? `${school.distance_miles} mi away` : "Nearby"}
                          {school.grades ? ` · Grades ${school.grades}` : ""}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-white/45">
                    {isNycContextArea
                      ? "No nearby DOE school rows are currently available for this address."
                      : "NYC DOE school data is only available for the five boroughs."}
                  </p>
                )}
                {schoolsPayload.source && (
                  <p className="text-[11px] text-white/30 mt-4">Source: {schoolsPayload.source}</p>
                )}
              </div>

              {context && crime.available && (
                <div className="rounded-sm border border-white/5 bg-[#101116] p-5" data-testid="detail-crime-context">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between mb-4">
                    <div>
                      <p className="text-[11px] uppercase tracking-[0.18em] text-white/35 flex items-center gap-1.5">
                        <ShieldWarning size={12} weight="duotone" className="text-brand" /> Detail-page context only
                      </p>
                      <h3 className="text-white text-xl">Recent NYPD complaint counts</h3>
                    </div>
                    <p className="text-sm text-white/45">
                      ~{crime.radius_mi} mi radius · last {crime.months} months
                    </p>
                  </div>
                  <p className="text-white/70 text-sm mb-4">
                    <span className="text-white font-medium">{crime.total ?? 0}</span> NYPD complaint reports were logged within
                    ~{crime.radius_mi} miles of this point in the last {crime.months} months.
                  </p>
                  {crime.counts && (
                    <div className="grid gap-4 md:grid-cols-3">
                      {[
                        { key: "felony", label: "Felony" },
                        { key: "misdemeanor", label: "Misdemeanor" },
                        { key: "violation", label: "Violation" },
                      ].map(({ key, label }) => (
                        <div key={key} className="rounded-sm border border-white/10 bg-black/25 px-3 py-2 flex items-center justify-between">
                          <span className="text-white/70 text-sm">{label}</span>
                          <span className="text-white font-serif text-lg" data-testid={`crime-${key}`}>{crime.counts[key] ?? 0}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {crime.top_offenses?.length > 0 && (
                    <div className="mt-4">
                      <p className="text-[11px] uppercase tracking-[0.18em] text-white/35 mb-2">Most common reported offenses</p>
                      <div className="flex flex-wrap gap-2">
                        {crime.top_offenses.map((item) => (
                          <span key={item.label} className="rounded-sm border border-white/10 px-2.5 py-1 text-xs text-white/65">
                            {item.label} ({item.count})
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  <p className="text-xs text-white/45 mt-4 flex items-start gap-2" data-testid="crime-disclaimer">
                    <Info size={14} weight="duotone" className="text-brand shrink-0 mt-0.5" />
                    <span>{crime.disclaimer}</span>
                  </p>
                  {crime.source && (
                    <p className="text-[11px] text-white/30 mt-2">Source: {crime.source}</p>
                  )}
                </div>
              )}
            </section>

            {property.amenities?.length > 0 && (
              <div data-testid="detail-amenities">
                <h2 className="text-sm uppercase tracking-[0.18em] text-white/50 mb-3">Amenities</h2>
                <div className="flex flex-wrap gap-2">
                  {property.amenities.map((a) => (
                    <span
                      key={a}
                      className="px-2.5 py-1 text-[11px] uppercase tracking-wider rounded-sm border border-white/10 text-white/65"
                    >
                      {String(a).replace(/_/g, " ")}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {property.features?.length > 0 && (
              <div>
                <h2 className="text-sm uppercase tracking-[0.18em] text-white/50 mb-3">Features</h2>
                <div className="grid sm:grid-cols-2 gap-2">
                  {property.features.map((f) => (
                    <span key={f} className="flex items-center gap-2 text-white/70 text-sm"><Check size={16} weight="bold" className="text-brand" /> {f}</span>
                  ))}
                </div>
              </div>
            )}

            {property.tour_url && (
              <div data-testid="virtual-tour">
                <h2 className="text-sm uppercase tracking-[0.18em] text-white/50 mb-3">Virtual walkthrough</h2>
                <div className="relative aspect-video overflow-hidden rounded-sm border border-white/10 bg-black">
                  <iframe
                    src={tourEmbedSrc(property.tour_url)}
                    title="Virtual tour"
                    className="absolute inset-0 h-full w-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; xr-spatial-tracking; fullscreen"
                    allowFullScreen
                  />
                </div>
              </div>
            )}

            {property.listing_type !== "rent" && <MortgageCalculator price={property.price} />}
          </div>

          {/* Right: inquiry */}
          <div className="lg:col-span-1">
            <div className="sticky top-28 bg-[#101116] border border-white/5 rounded-sm p-6">
              <InquiryForm property={property} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
